// The AI model choice of the render studio (W15: GET /v1/render/engines). The list comes from the
// server: which engines exist, which are switched on, and which one it uses when none is named.

import type { Schemas } from "@artisan/api-client";
import { cn } from "@artisan/ui";
import { ChevronDown, Lock } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { api, unwrap } from "../lib/api";

export type Engine = Schemas["EngineOut"];

const TAGS: Record<string, string> = { precise: "Нарийвчлал", quality: "Чанартай", fast: "Хурдан" };
const TAG_STYLE: Record<string, string> = {
  precise: "bg-primary-soft text-primary",
  quality: "bg-amber-100 text-amber-800",
  fast: "bg-emerald-100 text-emerald-800",
};
const REASONS: Record<string, string> = { coming_soon: "Удахгүй", not_configured: "Түлхүүр тохируулаагүй", disabled: "Идэвхгүй" };

/** Ready-made wishes: one tap adds them to the note. */
export const NOTE_CHIPS = ["Өдрийн гэрэл", "Үдшийн дулаан гэрэл", "Минимал", "Тансаг материал"];

/** The server's engines, once per screen. null while loading or when it cannot be reached
 * (offline): the render then simply uses the server's own default. */
export function useEngines(): Engine[] | null {
  const [engines, setEngines] = useState<Engine[] | null>(null);
  useEffect(() => {
    let live = true;
    unwrap(api().GET("/v1/render/engines"))
      .then((r) => live && setEngines(r.items))
      .catch(() => live && setEngines(null));
    return () => {
      live = false;
    };
  }, []);
  return engines;
}

/** The engine in use: the rep's choice if it still exists, else the server's default. */
export function currentEngine(engines: Engine[] | null, id: string | null): Engine | null {
  if (!engines?.length) return null;
  return engines.find((e) => e.id === id) ?? engines.find((e) => e.default) ?? engines.find((e) => e.available) ?? engines[0] ?? null;
}

export function engineLabel(engines: Engine[] | null, id: string | null | undefined): string | null {
  if (!id) return null;
  return engines?.find((e) => e.id === id)?.label ?? id;
}

export function EnginePicker({
  engines,
  engineId,
  resolution,
  itemCount,
  onEngine,
  onResolution,
  onChip,
}: {
  engines: Engine[];
  engineId: string | null;
  resolution: string | null;
  itemCount: number;
  onEngine: (id: string) => void;
  onResolution: (r: string) => void;
  onChip: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<"image" | "video">("image");
  const box = useRef<HTMLDivElement>(null);
  const cur = currentEngine(engines, engineId);
  const res = resolution && cur?.resolutions.includes(resolution) ? resolution : (cur?.default_resolution ?? null);

  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("pointerdown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  if (!cur) return null;
  const list = engines.filter((e) => e.kind === kind);
  const anyReady = engines.some((e) => e.available);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-11 max-w-[180px] items-center gap-1.5 rounded-xl border px-3 text-[13px] whitespace-nowrap"
      >
        <span className="text-muted-foreground">Загвар</span>
        <b className="truncate font-semibold">{cur.label}</b>
        {res ? <span className="text-muted-foreground">· {res}</span> : null}
        <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {open ? (
        <div role="dialog" aria-label="AI загвар сонгох" className="absolute bottom-[calc(100%+10px)] left-0 z-20 grid w-[400px] gap-3 rounded-2xl border bg-background p-3.5 shadow-xl">
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-muted p-1" role="tablist" aria-label="Төрөл">
            {(
              [
                ["image", "Зураг"],
                ["video", "Видео"],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={kind === k}
                onClick={() => setKind(k)}
                className={cn("h-9 rounded-lg text-sm font-semibold", kind === k ? "bg-background shadow-xs" : "text-muted-foreground")}
              >
                {label}
              </button>
            ))}
          </div>
          {!anyReady ? <p className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">Одоогоор серверт ямар ч AI загвар асаагаагүй байна. Админ түлхүүр тохируулсны дараа ажиллана.</p> : null}
          <ul className="grid gap-2" aria-label="Загварууд">
            {list.map((e) => {
              const picked = e.id === cur.id;
              return (
                <li key={e.id}>
                  <button
                    type="button"
                    disabled={!e.available}
                    aria-pressed={picked}
                    onClick={() => onEngine(e.id)}
                    className={cn(
                      "flex w-full items-start gap-3 rounded-xl border p-3 text-left",
                      picked && "border-primary ring-2 ring-primary/25",
                      !e.available && "cursor-not-allowed opacity-55",
                    )}
                  >
                    <span className="grid size-9 shrink-0 place-items-center rounded-lg bg-muted text-sm font-bold" aria-hidden>
                      {e.label.charAt(0)}
                    </span>
                    <span className="grid min-w-0 flex-1 gap-1.5">
                      <span className="flex items-baseline justify-between gap-2">
                        <b className="text-sm font-semibold">{e.label}</b>
                        <span className="text-xs text-muted-foreground">{e.vendor}</span>
                      </span>
                      <span className="flex flex-wrap gap-1.5">
                        {e.available ? (
                          e.tags.map((t) => (
                            <span key={t} className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", TAG_STYLE[t] ?? "bg-muted text-muted-foreground")}>
                              {TAGS[t] ?? t}
                            </span>
                          ))
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                            <Lock className="size-3" aria-hidden /> {REASONS[e.unavailable_reason ?? ""] ?? "Ашиглах боломжгүй"}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {cur.kind === "image" && cur.resolutions.length ? (
            <div className="grid gap-1.5">
              <span className="text-xs text-muted-foreground">Нягтрал</span>
              <div className="inline-flex w-fit gap-0.5 rounded-xl border p-1" role="group" aria-label="Нягтрал">
                {cur.resolutions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    aria-pressed={res === r}
                    onClick={() => onResolution(r)}
                    className={cn("h-8 rounded-lg px-3.5 text-sm font-semibold", res === r ? "bg-primary-soft text-primary" : "text-muted-foreground")}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          {cur.max_images > 0 && itemCount > cur.max_images ? (
            <p className="rounded-lg bg-amber-100 px-3 py-2 text-xs text-amber-900">
              {cur.label} нэг удаад {cur.max_images} зураг хүртэл авна, одоо {itemCount} бараа байрлуулсан байна. Барааг цөөлөх эсвэл өөр загвар сонгоно уу.
            </p>
          ) : null}
          <div className="grid gap-1.5">
            <span className="text-xs text-muted-foreground">Бэлэн тайлбар</span>
            <div className="flex flex-wrap gap-1.5">
              {NOTE_CHIPS.map((c) => (
                <button key={c} type="button" onClick={() => onChip(c)} className="rounded-full bg-muted px-3 py-1.5 text-[13px] font-medium">
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
